import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Observable, of } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { JobTask, TaskMaster, TaskStatus } from 'src/app/types/dto/tasks.dto'
import { ProcessedJobTask } from 'src/app/types/datasources/tasks.model'
import { environment } from 'src/environments/environment'
import { AuthService } from '../auth.service'
import { HttpHelperService } from '../http.service'
import { TimedCache } from '../timed-cache'
import { LogService } from '../log.service'
import { JobLookupApiService } from './job-lookup.api.service'

@Injectable({
  providedIn: 'root',
})
export class TasksApiService {
  private httpHelper = inject(HttpHelperService);
  private auth = inject(AuthService);
  private logger = inject(LogService);
  private http = inject(HttpClient);
  private jobLookup = inject(JobLookupApiService);

  private cachedTaskMasters = new TimedCache<TaskMaster[]>()
  private cachedTaskCodes = new TimedCache<string[]>()
  private cachedTasks = new TimedCache<ProcessedJobTask[]>()

  constructor() {
    this.httpHelper.clearCaches.subscribe(() => {
      this.cachedTaskMasters.clear()
      this.cachedTaskCodes.clear()
      this.cachedTasks.clear()
    })
  }

  // only getting active task masters
  getTaskMastersForCurrentCompany(): Observable<TaskMaster[]> {
    const comp = this.auth.getCurrentCompany()
    if (!comp) {
      this.logger.log('tasks.api', 'getTaskMastersForCurrentCompany', null, 'no company set')
      throw Error('no company set in getTaskMastersForCurrentCompany')
    }
    return this.cachedTaskMasters.get(comp.id, () =>
      this.http
        .get<TaskMaster[]>(environment.apiUrl + 'task-masters', this.httpHelper.httpReadOnlyOptions)
        .pipe(
          map((res) => {
            const active = res.filter((t) => t.isActive)    
            return active.sort((a, b) => a.taskTitle.localeCompare(b.taskTitle))
          })
        )
    )
  }

  getTaskCodes(): Observable<string[]> {
    const comp = this.auth.getCurrentCompany()
    if (!comp) {
      this.logger.log('tasks.api', 'getTaskCodes', null, 'no company set')
      throw Error('no company set in getTaskCodes')
    }

    return this.cachedTaskCodes.get(comp.id, () =>
      this.http
        .get<string[]>(environment.apiUrl + 'task-masters/task-codes', this.httpHelper.httpReadOnlyOptions)
        .pipe(map((res) => res.sort((a, b) => a.localeCompare(b))))
    )
  }

  getTasksForTaskmaster(taskMasterId: number): Observable<ProcessedJobTask[]> {
    const comp = this.auth.getCurrentCompany()
    if (!comp) {
      this.logger.log('tasks.api', 'getTasksForTaskmaster', null, 'no company set')
      throw Error('no company set in getTasksForTaskmaster')
    }

    return this.cachedTasks.get(`${comp.id}:task-master:${taskMasterId}`, () => {
      return this.getTaskMastersForCurrentCompany().pipe(
        switchMap((taskMasters) => {
          const taskMaster = taskMasters.find((tm) => tm.id === taskMasterId)
          const taskType = taskMaster?.taskTitle ?? ''
          return this.http
            .get<JobTask[]>(
              environment.apiUrl + 'job-tasks/task-master/' + taskMasterId,
              this.httpHelper.httpReadOnlyOptions
            )
            .pipe(
              map((res) => this.mapJobTasks(res, taskType)),
              switchMap((tasks) => this.jobLookup.enrich(tasks))
            )
        })
      )
    })
  }

  getTasksForTaskCode(taskCode: string): Observable<ProcessedJobTask[]> {
    const comp = this.auth.getCurrentCompany()
    if (!comp) {
      this.logger.log('tasks.api', 'getTasksForTaskCode', null, 'no company set')
      throw Error('no company set in getTasksForTaskCode')
    }

    return this.cachedTasks.get(`${comp.id}:task-code:${taskCode}`, () =>
      this.http
        .get<JobTask[]>(
          environment.apiUrl + 'job-tasks/task-code/' + encodeURIComponent(taskCode),
          this.httpHelper.httpReadOnlyOptions
        )
        .pipe(
          map((res) => this.mapJobTasks(res, taskCode)),
          switchMap((tasks) => this.jobLookup.enrich(tasks))
        )
    )
  }

  private mapJobTasks(tasks: JobTask[], fallbackTaskType: string): ProcessedJobTask[] {
    return tasks.map((jt) => ({
      ...jt,
      taskType: jt.taskMasterTitle ?? fallbackTaskType,
      startDate: jt.startDate ? new Date(jt.startDate) : undefined,
      endDate: jt.endDate ? new Date(jt.endDate) : undefined,
      sentDate: jt.sentDate ? new Date(jt.sentDate) : undefined,
      status: TaskStatus[jt.statusId],
    }))
  }

  mock1() {
    return of([
      {
        id: 1,
        jobId: 200,
        jobNumber: '200',
        startDate: new Date('2022-01-01T00:00:00'),
        endDate: new Date('2022-02-01T00:00:00'),
        status: 'blah',
        allocatedTo: '',
        division: '',
        vendor: '',
        salesRep: '',
        siteManager: '',
      },
      {
        id: 2,
        jobId: 200,
        jobNumber: '200',
        startDate: new Date('2022-01-03T00:00:00'),
        endDate: new Date('2022-02-03T00:00:00'),
        status: 'blah',
        allocatedTo: '',
        division: '',
        vendor: '',
        salesRep: '',
        siteManager: '',
      },
      {
        id: 3,
        jobId: 100,
        jobNumber: '100',
        startDate: new Date('2022-02-01T00:00:00'),
        endDate: new Date('2022-03-01T00:00:00'),
        status: 'blah',
        allocatedTo: '',
        division: '',
        vendor: '',
        salesRep: '',
        siteManager: '',
      },
      {
        id: 3,
        jobId: 201,
        jobNumber: '201',
        startDate: new Date('2022-02-01T00:00:00'),
        endDate: new Date('2022-03-01T00:00:00'),
        status: 'blah',
        allocatedTo: '',
        division: '',
        vendor: '',
        salesRep: '',
        siteManager: '',
      },
      {
        id: 6,
        jobId: 500,
        jobNumber: '500',
        startDate: new Date('2022-03-20T00:00:00'),
        endDate: new Date('2022-04-20T00:00:00'),
        status: 'blah',
        allocatedTo: '',
        division: '',
        vendor: '',
        salesRep: '',
        siteManager: '',
      },
    ])
  }

  mock2() {
    return of([
      // date missing (task not finished) so entire job 100 shouldnt count
      {
        id: 4,
        jobId: 100,
        jobNumber: '100',
        startDate: new Date('2022-01-01T00:00:00'),
        endDate: undefined,
        status: 'blah',
        allocatedTo: '',
        division: '',
        vendor: '',
        salesRep: '',
        siteManager: '',
      },
      {
        id: 5,
        jobId: 100,
        jobNumber: '100',
        startDate: new Date('2022-03-03T00:00:00'),
        endDate: new Date('2022-04-03T00:00:00'),
        status: 'blah',
        allocatedTo: '',
        division: '',
        vendor: '',
        salesRep: '',
        siteManager: '',
      },
      {
        id: 6,
        jobId: 200,
        jobNumber: '200',
        startDate: new Date('2022-03-09T00:00:00'),
        endDate: new Date('2022-04-09T00:00:00'),
        status: 'blah',
        allocatedTo: '',
        division: '',
        vendor: '',
        salesRep: '',
        siteManager: 'bob',
      },
      {
        id: 6,
        jobId: 200,
        jobNumber: '200',
        startDate: new Date('2022-03-09T00:00:00'),
        endDate: new Date('2022-04-10T00:00:00'),
        status: 'blah',
        allocatedTo: '',
        division: '',
        vendor: '',
        salesRep: '',
        siteManager: 'sally',
      },
      // later task for job 201 is in next period, this one shouldn't count
      {
        id: 6,
        jobId: 201,
        jobNumber: '201',
        startDate: new Date('2022-03-15T00:00:00'),
        endDate: new Date('2022-04-15T00:00:00'),
        status: 'blah',
        allocatedTo: '',
        division: '',
        vendor: '',
        salesRep: '',
        siteManager: '',
      },
      // no task 1 equivalent so ignored (shows as 0 in graph if grouping by job):
      {
        id: 6,
        jobId: 202,
        jobNumber: '202',
        startDate: new Date('2022-03-20T00:00:00'),
        endDate: new Date('2022-04-20T00:00:00'),
        status: 'blah',
        allocatedTo: '',
        division: '',
        vendor: '',
        salesRep: '',
        siteManager: '',
      },
      // period 2 (JUNE)
      {
        id: 6,
        jobId: 500,
        jobNumber: '500',
        startDate: new Date('2022-05-20T00:00:00'),
        endDate: new Date('2022-06-20T00:00:00'),
        status: 'blah',
        allocatedTo: '',
        division: '',
        vendor: '',
        salesRep: '',
        siteManager: 'sally',
      },
      {
        id: 6,
        jobId: 201,
        jobNumber: '201',
        startDate: new Date('2022-05-20T00:00:00'),
        endDate: new Date('2022-06-25T00:00:00'),
        status: 'blah',
        allocatedTo: '',
        division: '',
        vendor: '',
        salesRep: '',
        siteManager: 'bob',
      },
    ])
  }
}
